import { useQuery } from "@tanstack/react-query";

import { TimeRange } from "../../utils/timerange";
import {
  AggregatedAttributePerformanceResponse,
  AggregatedLensPerformanceStats,
  getAggregatedAttributePerformanceStats,
  getAggregatedLensPerformanceStats,
  getLensPerformanceSparklineData,
  getLensPerformanceTimelineData,
  LensPerformanceSparklineResponse,
  LensPerformanceTimelineData
} from "../queries/performanceAnalysis";

interface UseAggregatedLensPerformanceParams {
  timeRange: TimeRange;
  timezone: string;
  userFilter?: string;
  lensFilter?: string;
  permittedEmails?: string[] | null;
}

interface UseSparklineDataParams {
  userFilter?: string;
  lensFilter?: string;
  permittedEmails?: string[] | null;
  enabled?: boolean;
}

interface UseLensTimelineDataParams {
  timeRange: TimeRange;
  timezone: string;
  lensName: string;
  userFilter?: string;
  permittedEmails?: string[] | null;
  enabled?: boolean;
}

export const useAggregatedLensPerformance = ({
  timeRange,
  timezone,
  userFilter = "all",
  lensFilter = "all",
  permittedEmails = null
}: UseAggregatedLensPerformanceParams) => {
  return useQuery({
    queryKey: ["aggregatedLensPerformance", timeRange, timezone, userFilter, lensFilter, permittedEmails],
    queryFn: (): Promise<AggregatedLensPerformanceStats> => getAggregatedLensPerformanceStats({
      timeRange,
      timezone,
      userFilter,
      lensFilter,
      permittedEmails
    }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

export const useSparklineData = ({
  userFilter = "all",
  lensFilter = "all",
  permittedEmails = null,
  enabled = true
}: UseSparklineDataParams) => {
  return useQuery({
    queryKey: ["sparklineData", userFilter, lensFilter, permittedEmails],
    queryFn: (): Promise<LensPerformanceSparklineResponse> => getLensPerformanceSparklineData({
      userFilter,
      lensFilter,
      permittedEmails
    }),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes (longer than main data since it's historical)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

export const useLensTimelineData = ({
  timeRange,
  timezone,
  lensName,
  userFilter = "all",
  permittedEmails = null,
  enabled = true
}: UseLensTimelineDataParams) => {
  return useQuery({
    queryKey: ["lensTimelineData", timeRange, timezone, lensName, userFilter, permittedEmails],
    queryFn: (): Promise<LensPerformanceTimelineData> => getLensPerformanceTimelineData({
      timeRange,
      timezone,
      lensName,
      userFilter,
      permittedEmails
    }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

interface UseAttributePerformanceParams {
  timeRange: TimeRange;
  timezone: string;
  lensFilter: string;
  userFilter?: string;
  permittedEmails?: string[] | null;
  minCalls?: number;
  enabled?: boolean;
}

export const useAttributePerformance = ({
  timeRange,
  timezone,
  lensFilter,
  userFilter = "all",
  permittedEmails = null,
  minCalls = 10,
  enabled = true
}: UseAttributePerformanceParams) => {
  return useQuery({
    queryKey: ["attributePerformance", timeRange, timezone, lensFilter, userFilter, permittedEmails, minCalls],
    queryFn: (): Promise<AggregatedAttributePerformanceResponse> => getAggregatedAttributePerformanceStats({
      timeRange,
      timezone,
      lensFilter,
      userFilter,
      permittedEmails,
      minCalls
    }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};
